import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity,
  CloudOff,
  Crown,
  Eye,
  Flame,
  Heart,
  Landmark,
  Loader2,
  ShieldAlert,
  Ticket,
  Trophy,
  Users,
} from "lucide-react";
import { fetchHouseAnalytics } from "../../redux/operatorConsoleSlice";
import { formatCash } from "../../utils/operatorConsoleHelpers";

const RANGES = [
  { id: "7", label: "7D" },
  { id: "30", label: "30D" },
  { id: "90", label: "90D" },
  { id: "all", label: "All" },
];

const CHART_MODES = [
  { id: "cumulative", label: "House P&L" },
  { id: "daily", label: "Daily net" },
  { id: "flow", label: "Money flow" },
];

const VIEW_W = 640;
const VIEW_H = 230;
const PAD_TOP = 14;
const PAD_BOTTOM = 22;

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const compact = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1000).toFixed(0)}K`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const dayLabel = (dateKey) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateKey || ""));
  if (!match) return String(dateKey || "");
  const dt = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const toneOf = (v) => (v > 0 ? "oc-ab-up" : v < 0 ? "oc-ab-down" : "");

/* ─────────────────────── Performance chart ─────────────────────── */

const PerformanceChart = ({ timeline, mode }) => {
  const gradId = useId();
  const surfaceRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const n = timeline.length;
  const hasActivity = timeline.some(
    (p) => p.net !== 0 || p.stakeSettled !== 0 || p.payoutOut !== 0 || p.placedCount !== 0
  );

  const { yMin, yMax } = useMemo(() => {
    let lo = 0;
    let hi = 0;
    for (const p of timeline) {
      if (mode === "cumulative") {
        lo = Math.min(lo, p.cumulativeNet);
        hi = Math.max(hi, p.cumulativeNet);
      } else if (mode === "daily") {
        lo = Math.min(lo, p.net);
        hi = Math.max(hi, p.net);
      } else {
        lo = Math.min(lo, -p.payoutOut);
        hi = Math.max(hi, p.stakeSettled);
      }
    }
    if (lo === 0 && hi === 0) hi = 1;
    const pad = (hi - lo) * 0.08;
    return { yMin: lo === 0 ? 0 : lo - pad, yMax: hi === 0 ? 0 : hi + pad };
  }, [timeline, mode]);

  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM;
  const span = Math.max(yMax - yMin, 1e-9);
  const toY = useCallback(
    (v) => PAD_TOP + (1 - (v - yMin) / span) * innerH,
    [yMin, span, innerH]
  );
  const zeroY = toY(0);
  const slotW = n > 0 ? VIEW_W / n : VIEW_W;
  const centerX = useCallback((i) => (i + 0.5) * slotW, [slotW]);

  const { linePath, areaPath } = useMemo(() => {
    if (mode !== "cumulative" || !n) return { linePath: "", areaPath: "" };
    const pts =
      n === 1
        ? [
            { x: 0, y: toY(timeline[0].cumulativeNet) },
            { x: VIEW_W, y: toY(timeline[0].cumulativeNet) },
          ]
        : timeline.map((p, i) => ({ x: centerX(i), y: toY(p.cumulativeNet) }));
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i += 1) {
      d += ` L ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`;
    }
    const base = Math.min(Math.max(zeroY, PAD_TOP), VIEW_H - PAD_BOTTOM);
    return {
      linePath: d,
      areaPath: `${d} L ${pts[pts.length - 1].x.toFixed(1)} ${base.toFixed(1)} L ${pts[0].x.toFixed(1)} ${base.toFixed(1)} Z`,
    };
  }, [mode, n, timeline, centerX, toY, zeroY]);

  const last = n ? timeline[n - 1].cumulativeNet : 0;
  const lineTone = last > 0 ? "up" : last < 0 ? "down" : "flat";

  const handleMove = (event) => {
    if (!n || !surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    setHoverIndex(Math.min(n - 1, Math.floor(frac * n)));
  };

  const hover = hoverIndex != null ? timeline[hoverIndex] : null;
  const hoverXFrac = hoverIndex != null ? (hoverIndex + 0.5) / n : 0;

  const axisIndices = useMemo(() => {
    if (n <= 1) return n === 1 ? [0] : [];
    if (n <= 8) return timeline.map((_, i) => i);
    const out = [];
    for (let k = 0; k < 5; k += 1) out.push(Math.round((k / 4) * (n - 1)));
    return [...new Set(out)];
  }, [n, timeline]);

  if (!n || !hasActivity) {
    return (
      <div className="oc-ab-chart-empty">
        <Activity size={15} />
        <span>No settled play in this window yet — the graph draws as tickets settle.</span>
      </div>
    );
  }

  const barW = Math.min(slotW * 0.56, 30);
  const flowBarW = Math.min(slotW * 0.3, 14);

  return (
    <div
      ref={surfaceRef}
      className="oc-ab-chart-surface"
      onPointerMove={handleMove}
      onPointerLeave={() => setHoverIndex(null)}
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="oc-ab-chart-svg"
        role="img"
        aria-label="House performance chart"
      >
        <defs>
          <linearGradient id={`${gradId}-up`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#46c479" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#46c479" stopOpacity="0.01" />
          </linearGradient>
          <linearGradient id={`${gradId}-down`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef5350" stopOpacity="0.01" />
            <stop offset="100%" stopColor="#ef5350" stopOpacity="0.26" />
          </linearGradient>
          <linearGradient id={`${gradId}-flat`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#64b5f6" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {[yMax, (yMax + yMin) / 2, yMin, ...(yMin < 0 && yMax > 0 ? [0] : [])].map(
          (level, i) => (
            <line
              key={`${level}-${i}`}
              x1={0}
              x2={VIEW_W}
              y1={toY(level)}
              y2={toY(level)}
              className={`oc-ab-grid${level === 0 ? " oc-ab-grid-zero" : ""}`}
              vectorEffect="non-scaling-stroke"
            />
          )
        )}

        {mode === "cumulative" ? (
          <>
            <path d={areaPath} fill={`url(#${gradId}-${lineTone})`} />
            <path
              d={linePath}
              className={`oc-ab-line oc-ab-line-${lineTone}`}
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
          </>
        ) : null}

        {mode === "daily"
          ? timeline.map((p, i) => {
              if (p.net === 0) return null;
              const y = toY(p.net);
              return (
                <rect
                  key={p.date}
                  x={centerX(i) - barW / 2}
                  y={Math.min(y, zeroY)}
                  width={barW}
                  height={Math.max(Math.abs(zeroY - y), 1.5)}
                  rx={2.5}
                  className={p.net > 0 ? "oc-ab-bar-up" : "oc-ab-bar-down"}
                  opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.45}
                />
              );
            })
          : null}

        {mode === "flow"
          ? timeline.map((p, i) => {
              const cx = centerX(i);
              const dim = hoverIndex != null && hoverIndex !== i;
              return (
                <g key={p.date} opacity={dim ? 0.45 : 1}>
                  {p.stakeSettled > 0 ? (
                    <rect
                      x={cx - flowBarW - 1}
                      y={toY(p.stakeSettled)}
                      width={flowBarW}
                      height={Math.max(zeroY - toY(p.stakeSettled), 1.5)}
                      rx={2}
                      className="oc-ab-bar-up"
                    />
                  ) : null}
                  {p.payoutOut > 0 ? (
                    <rect
                      x={cx + 1}
                      y={zeroY}
                      width={flowBarW}
                      height={Math.max(toY(-p.payoutOut) - zeroY, 1.5)}
                      rx={2}
                      className="oc-ab-bar-down"
                    />
                  ) : null}
                </g>
              );
            })
          : null}

        {hover ? (
          <line
            x1={hoverXFrac * VIEW_W}
            x2={hoverXFrac * VIEW_W}
            y1={PAD_TOP}
            y2={VIEW_H - PAD_BOTTOM}
            className="oc-ab-crosshair"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>

      <div className="oc-ab-yaxis" aria-hidden>
        <span>{compact(yMax)}</span>
        {yMin < 0 && yMax > 0 ? <span>0</span> : null}
        <span>{compact(yMin)}</span>
      </div>

      <div className="oc-ab-xaxis" aria-hidden>
        {axisIndices.map((i) => (
          <span key={timeline[i]?.date || i} style={{ left: `${((i + 0.5) / n) * 100}%` }}>
            {dayLabel(timeline[i]?.date)}
          </span>
        ))}
      </div>

      {hover ? (
        <div
          className={`oc-ab-tooltip tabular-nums${hoverXFrac > 0.7 ? " oc-ab-tooltip-left" : ""}`}
          style={{ left: `${hoverXFrac * 100}%` }}
        >
          <em>{dayLabel(hover.date)}</em>
          {mode === "flow" ? (
            <>
              <span className="oc-ab-up">Kept {compact(hover.stakeSettled)}</span>
              <span className="oc-ab-down">Paid {compact(hover.payoutOut)}</span>
              <strong className={toneOf(hover.net)}>Net {compact(hover.net)}</strong>
            </>
          ) : (
            <>
              <strong
                className={toneOf(mode === "cumulative" ? hover.cumulativeNet : hover.net)}
              >
                {compact(mode === "cumulative" ? hover.cumulativeNet : hover.net)}
              </strong>
              <span>
                {hover.settledCount} settled · {hover.placedCount} placed
              </span>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

/* ─────────────────────── League intel ─────────────────────── */

const LEAGUE_VIEWS = [
  { id: "edge", label: "House edge" },
  { id: "handle", label: "Handle" },
  { id: "board", label: "Board mix" },
];

const LeagueIntel = ({ leagues, boardLeagues }) => {
  const [view, setView] = useState("edge");

  const rows = useMemo(() => {
    if (view === "board") {
      return (boardLeagues || []).map((l) => ({
        key: String(l.leagueId || l.name),
        name: l.name,
        logo: l.logo,
        value: l.fixtureCount,
        valueLabel: `${l.fixtureCount} fixture${l.fixtureCount === 1 ? "" : "s"}`,
        hint: `${l.upcomingCount} upcoming · ${l.bettorsCount} bettors`,
        tone: "",
      }));
    }
    if (view === "handle") {
      return (leagues || []).map((l) => ({
        key: String(l.leagueId || l.name),
        name: l.name,
        logo: l.logo,
        value: l.stake,
        valueLabel: compact(l.stake),
        hint: `${l.ticketCount} tickets · ${l.legCount} picks`,
        tone: "",
      }));
    }
    return [...(leagues || [])]
      .filter((l) => l.settledStake > 0 || l.payout > 0)
      .sort((a, b) => a.net - b.net)
      .map((l) => ({
        key: String(l.leagueId || l.name),
        name: l.name,
        logo: l.logo,
        value: l.net,
        valueLabel: `${l.net > 0 ? "+" : ""}${compact(l.net)}`,
        hint:
          l.net < 0
            ? `Bettors beat you here · ${l.wonLegs} legs won`
            : `House kept the edge · ${l.lostLegs} legs lost`,
        tone: toneOf(l.net),
      }));
  }, [view, leagues, boardLeagues]);

  const peak = Math.max(...rows.map((r) => Math.abs(Number(r.value) || 0)), 1);

  return (
    <section className="oc-ab-card">
      <header className="oc-ab-card-head">
        <div>
          <span className="oc-ab-kicker">League intel</span>
          <p className="oc-ab-caption">
            {view === "edge"
              ? "Where you win and where losses come from"
              : view === "handle"
              ? "Where bettor money goes on your board"
              : "Leagues you add most to the board"}
          </p>
        </div>
        <div className="oc-ab-toggle-row">
          {LEAGUE_VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`oc-ab-toggle${view === v.id ? " oc-ab-toggle-on" : ""}`}
              onClick={() => setView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </header>

      {!rows.length ? (
        <p className="oc-ab-empty-copy">
          {view === "board"
            ? "Add fixtures to the board to see your league mix."
            : "League intel appears once tickets are placed on your fixtures."}
        </p>
      ) : (
        <ul className="oc-ab-league-list">
          {rows.slice(0, 8).map((row) => {
            const width = Math.max((Math.abs(Number(row.value) || 0) / peak) * 100, 4);
            return (
              <li key={row.key} className="oc-ab-league-row">
                {row.logo ? (
                  <img src={row.logo} alt="" className="oc-ab-league-logo" />
                ) : (
                  <span className="oc-ab-league-logo oc-ab-league-logo-blank">
                    <Trophy size={11} />
                  </span>
                )}
                <div className="oc-ab-league-copy">
                  <div className="oc-ab-league-top">
                    <strong>{row.name}</strong>
                    <em className={`tabular-nums ${row.tone}`}>{row.valueLabel}</em>
                  </div>
                  <span className="oc-ab-league-track" aria-hidden>
                    <span
                      className={row.tone === "oc-ab-down" ? "oc-ab-track-down" : row.tone === "oc-ab-up" ? "oc-ab-track-up" : "oc-ab-track-flat"}
                      style={{ width: `${width}%` }}
                    />
                  </span>
                  <span className="oc-ab-league-hint">{row.hint}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

/* ─────────────────────── Audience funnel ─────────────────────── */

const AudienceFunnel = ({ dashboard }) => {
  const company = dashboard?.company || {};
  const customers = dashboard?.customers || {};
  const activity = dashboard?.activity || {};

  const stages = [
    { id: "viewers", Icon: Eye, label: "Viewers", value: Number(company.viewers) || 0, hint: `${Number(company.totalViews) || 0} total views` },
    { id: "followers", Icon: Heart, label: "Followers", value: Number(company.followers) || 0 },
    { id: "bettors", Icon: Users, label: "Bettors", value: Number(customers.totalUnique) || 0 },
    { id: "repeat", Icon: Crown, label: "Repeat bettors", value: Number(customers.repeatBettors) || 0 },
  ];
  const top = Math.max(...stages.map((s) => s.value), 1);

  const conv = (i) => {
    if (i === 0 || !stages[i - 1].value) return null;
    return Math.round((stages[i].value / stages[i - 1].value) * 100);
  };

  return (
    <section className="oc-ab-card">
      <header className="oc-ab-card-head">
        <div>
          <span className="oc-ab-kicker">Audience funnel</span>
          <p className="oc-ab-caption">How attention converts into play</p>
        </div>
        <span className="oc-ab-funnel-tickets">
          <Ticket size={11} />
          {Number(activity.totalTickets) || 0} tickets
        </span>
      </header>

      <div className="oc-ab-funnel">
        {stages.map((stage, i) => {
          const pct = conv(i);
          return (
            <div key={stage.id} className="oc-ab-funnel-stage">
              <div className="oc-ab-funnel-meta">
                <span className="oc-ab-funnel-label">
                  <stage.Icon size={11} />
                  {stage.label}
                </span>
                <strong className="tabular-nums">{stage.value.toLocaleString()}</strong>
              </div>
              <div className="oc-ab-funnel-track">
                <span
                  className="oc-ab-funnel-fill"
                  style={{ width: `${Math.max((stage.value / top) * 100, stage.value > 0 ? 6 : 0)}%` }}
                />
              </div>
              <span className="oc-ab-funnel-hint">
                {pct != null ? `${pct}% of ${stages[i - 1].label.toLowerCase()}` : stage.hint || "Top of funnel"}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ─────────────────────── Main board ─────────────────────── */

const HouseAnalyticsBoard = ({ houseId, dashboard }) => {
  const dispatch = useDispatch();
  const { analytics, analyticsHouseId, analyticsStatus, analyticsError } = useSelector(
    (s) => s.operatorConsole
  );
  const [range, setRange] = useState("30");
  const [chartMode, setChartMode] = useState("cumulative");

  useEffect(() => {
    if (!houseId) return;
    dispatch(fetchHouseAnalytics({ houseId, days: range }));
  }, [dispatch, houseId, range]);

  const ready = analytics && String(analyticsHouseId) === String(houseId);
  const timeline = useMemo(() => (ready ? analytics.timeline || [] : []), [ready, analytics]);
  const lifetime = ready ? analytics.lifetime || {} : {};

  const rangeStats = useMemo(() => {
    let net = 0;
    let stakeSettled = 0;
    let payoutOut = 0;
    let placedStake = 0;
    let placedCount = 0;
    let best = null;
    let worst = null;
    for (const p of timeline) {
      net += p.net;
      stakeSettled += p.stakeSettled;
      payoutOut += p.payoutOut;
      placedStake += p.placedStake;
      placedCount += p.placedCount;
      if (p.net > 0 && (!best || p.net > best.net)) best = p;
      if (p.net < 0 && (!worst || p.net < worst.net)) worst = p;
    }
    return {
      net,
      stakeSettled,
      payoutOut,
      placedStake,
      placedCount,
      best,
      worst,
      hold: stakeSettled > 0 ? Math.round((net / stakeSettled) * 1000) / 10 : null,
    };
  }, [timeline]);

  const netTone = toneOf(rangeStats.net);
  const treasury = dashboard?.treasury || {};
  const available = Number(treasury.availableBalance) || 0;
  const openLiability = Number(lifetime.openLiability) || 0;
  const coverage =
    openLiability > 0 && available > 0
      ? Math.round((available / openLiability) * 10) / 10
      : null;

  const loading = analyticsStatus === "loading" && !ready;

  return (
    <div className="oc-ab">
      {/* ── Hero: house P&L for the window ── */}
      <section className="oc-ab-card oc-ab-hero">
        <div className="oc-ab-hero-main">
          <span className="oc-ab-kicker">
            House P&L · {RANGES.find((r) => r.id === range)?.label || range}
          </span>
          <strong className={`oc-ab-hero-value tabular-nums ${netTone}`}>
            {rangeStats.net > 0 ? "+" : rangeStats.net < 0 ? "−" : ""}
            {formatCash(Math.abs(rangeStats.net), { compact: true })}
          </strong>
          <span className="oc-ab-hero-sub">
            {rangeStats.hold != null
              ? `${rangeStats.hold}% hold on ${compact(rangeStats.stakeSettled)} settled handle`
              : "No settled handle in this window yet"}
          </span>
        </div>

        <div className="oc-ab-hero-tiles">
          <div className="oc-ab-tile">
            <span>Kept</span>
            <strong className="tabular-nums oc-ab-up">{compact(rangeStats.stakeSettled)}</strong>
          </div>
          <div className="oc-ab-tile">
            <span>Paid out</span>
            <strong className="tabular-nums oc-ab-down">{compact(rangeStats.payoutOut)}</strong>
          </div>
          <div className="oc-ab-tile">
            <span>New handle</span>
            <strong className="tabular-nums">{compact(rangeStats.placedStake)}</strong>
          </div>
          <div className="oc-ab-tile">
            <span>Tickets in</span>
            <strong className="tabular-nums">{rangeStats.placedCount.toLocaleString()}</strong>
          </div>
        </div>

        <div className="oc-ab-ranges" role="tablist" aria-label="Analytics range">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={range === r.id}
              className={`oc-ab-range${range === r.id ? " oc-ab-range-on" : ""}`}
              onClick={() => setRange(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </section>

      {/* ── Performance chart ── */}
      <section className="oc-ab-card">
        <header className="oc-ab-card-head">
          <div>
            <span className="oc-ab-kicker">Performance</span>
            <p className="oc-ab-caption">
              {chartMode === "flow"
                ? "Stakes you kept vs winnings you paid, day by day"
                : chartMode === "daily"
                ? "Daily house net from settlements"
                : "Running house profit and loss"}
            </p>
          </div>
          <div className="oc-ab-toggle-row">
            {CHART_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`oc-ab-toggle${chartMode === m.id ? " oc-ab-toggle-on" : ""}`}
                onClick={() => setChartMode(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="oc-ab-chart-empty">
            <Loader2 size={16} className="animate-spin" />
          </div>
        ) : analyticsStatus === "failed" && !ready ? (
          <div className="oc-ab-chart-empty">
            <CloudOff size={15} />
            <span>{analyticsError || "Could not load analytics."}</span>
            <button
              type="button"
              className="oc-ab-retry"
              onClick={() => dispatch(fetchHouseAnalytics({ houseId, days: range }))}
            >
              Retry
            </button>
          </div>
        ) : (
          <PerformanceChart timeline={timeline} mode={chartMode} />
        )}
      </section>

      {/* ── Insight tiles ── */}
      <div className="oc-ab-insights">
        <article className="oc-ab-insight">
          <span className="oc-ab-insight-icon oc-ab-up">
            <Flame size={12} />
          </span>
          <div>
            <span className="oc-ab-insight-label">Best day</span>
            <strong className={`tabular-nums ${rangeStats.best ? "oc-ab-up" : ""}`}>
              {rangeStats.best ? `+${compact(rangeStats.best.net)}` : "—"}
            </strong>
            <em>{rangeStats.best ? dayLabel(rangeStats.best.date) : "No green day yet"}</em>
          </div>
        </article>
        <article className="oc-ab-insight">
          <span className="oc-ab-insight-icon oc-ab-down">
            <ShieldAlert size={12} />
          </span>
          <div>
            <span className="oc-ab-insight-label">Toughest day</span>
            <strong className={`tabular-nums ${rangeStats.worst ? "oc-ab-down" : ""}`}>
              {rangeStats.worst ? compact(rangeStats.worst.net) : "—"}
            </strong>
            <em>{rangeStats.worst ? dayLabel(rangeStats.worst.date) : "No losing day"}</em>
          </div>
        </article>
        <article className="oc-ab-insight">
          <span className="oc-ab-insight-icon">
            <Trophy size={12} />
          </span>
          <div>
            <span className="oc-ab-insight-label">House win rate</span>
            <strong className="tabular-nums">
              {lifetime.houseWinRate != null ? `${lifetime.houseWinRate}%` : "—"}
            </strong>
            <em>{lifetime.settledCount || 0} settled lifetime</em>
          </div>
        </article>
        <article className="oc-ab-insight">
          <span className="oc-ab-insight-icon">
            <Landmark size={12} />
          </span>
          <div>
            <span className="oc-ab-insight-label">Liability cover</span>
            <strong className={`tabular-nums ${coverage != null && coverage < 1 ? "oc-ab-down" : ""}`}>
              {coverage != null ? `${coverage}×` : openLiability > 0 ? "—" : "Clear"}
            </strong>
            <em>
              {openLiability > 0
                ? `${compact(openLiability)} open vs ${compact(available)} available`
                : "No open liability"}
            </em>
          </div>
        </article>
      </div>

      {/* ── League intel + funnel ── */}
      <div className="oc-ab-duo">
        <LeagueIntel
          leagues={ready ? analytics.leagues : []}
          boardLeagues={ready ? analytics.boardLeagues : []}
        />
        <AudienceFunnel dashboard={dashboard} />
      </div>
    </div>
  );
};

export default HouseAnalyticsBoard;
